import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  LinearProgress,
  FormControlLabel,
  Paper,
  Popover,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { WarningAmberRounded } from '@mui/icons-material';
import type {
  AlignmentMatrix,
  AlignmentMatrixCell,
  AlignmentMatrixRow,
  CapCode,
  DeclarativeAlignmentMark,
} from '@dosfilos/domain';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { capApi } from '../../lib/api/cap.api';
import { useProgramCapTarget } from '../../hooks/use-program-cap-target';
import { CapPageHeader, CAP_CONTENT_MAX_WIDTH } from './components/CapPageHeader';
import { errorText } from '../../lib/error-text';

// ─── Alignment matrix (Phase E — CAP, PR5) — wireframe screen 3 ────────────
// Grid PLO × course. Cells are SETS: declared {I,T,P} (editable, stored in
// the course's CCD) ∪ derived {E} (read-only, from signature
// assignments). Derived on every read via capBuildAlignmentMatrix.

const DECLARABLE: DeclarativeAlignmentMark[] = ['I', 'T', 'P'];

interface CellEditor {
  anchor: HTMLElement;
  row: AlignmentMatrixRow;
  cell: AlignmentMatrixCell;
  draft: DeclarativeAlignmentMark[];
}

export function AlignmentMatrixPage(): JSX.Element {
  const { t } = useTranslation('cap');
  const navigate = useNavigate();
  const { programId } = useParams<{ programId: string }>();
  const { currentTenantId } = useTenantStore();

  const [capCode, setCapCode] = useState<CapCode>('BSC');
  // Default to the program's target CAP (still switchable).
  const capTarget = useProgramCapTarget(programId);
  useEffect(() => {
    if (capTarget) setCapCode(capTarget.capCode);
  }, [capTarget]);
  const [matrix, setMatrix] = useState<AlignmentMatrix | null>(null);
  // PLO texts (sourceOutcomeId → ES text at the program's level) so the coordinator
  // can read what each PLO code means without leaving the matrix.
  const [ploTexts, setPloTexts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<CellEditor | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  // Staleness guard: capCode flips (BSC default → program's capTarget) fire two
  // loads; without this the slower/older response can overwrite the newer one,
  // leaving the tab on DEP but the (empty) BSC matrix shown. Only the latest wins.
  const loadReq = useRef(0);
  const load = useCallback(async (): Promise<void> => {
    if (!currentTenantId || !programId) return;
    const req = ++loadReq.current;
    setLoading(true);
    setError(null);
    try {
      const m = await capApi.buildAlignmentMatrix({
        tenantId: currentTenantId,
        programId,
        capCode,
      });
      if (loadReq.current === req) setMatrix(m);
    } catch (e) {
      if (loadReq.current === req) setError(errorText(e));
    } finally {
      if (loadReq.current === req) setLoading(false);
    }
  }, [currentTenantId, programId, capCode]);

  useEffect(() => {
    void load();
  }, [load]);

  // Load PLO texts for the reference legend + header tooltips.
  const level = capTarget?.level ?? 'undergraduate';
  useEffect(() => {
    if (!currentTenantId || !programId) return;
    let alive = true;
    void getDocs(
      query(
        collection(db, `tenants/${currentTenantId}/programOutcomes`),
        where('programId', '==', programId),
        where('capCode', '==', capCode),
        orderBy('order'),
      ),
    ).then((snap) => {
      if (!alive) return;
      const texts: Record<string, string> = {};
      for (const d of snap.docs) {
        const data = d.data();
        const src = data['sourceOutcomeId'] as string;
        const variant = (data[level] ?? data['undergraduate']) as { es?: string } | undefined;
        if (src && variant?.es) texts[src] = variant.es;
      }
      setPloTexts(texts);
    });
    return () => {
      alive = false;
    };
  }, [currentTenantId, programId, capCode, level]);

  const openEditor = (
    e: React.MouseEvent<HTMLElement>,
    row: AlignmentMatrixRow,
    cell: AlignmentMatrixCell,
  ): void => {
    // A row is editable when the COURSE has a CCD. It used to also require a
    // template, because the marks were stored on one.
    if (!row.hasCcd) return; // gap row — nothing to edit
    setEditor({
      anchor: e.currentTarget,
      row,
      cell,
      draft: cell.marks.filter((m): m is DeclarativeAlignmentMark => m !== 'E'),
    });
  };

  const saveEditor = async (): Promise<void> => {
    if (!editor || !currentTenantId) return;
    setSaving(true);
    setError(null);
    try {
      // Rebuild the course's WHOLE declared alignment: every cell of this row
      // minus 'E' (derived), with the edited cell replaced by the draft.
      const alignment: Record<string, DeclarativeAlignmentMark[]> = {};
      for (const c of editor.row.cells) {
        const declared =
          c.outcomeId === editor.cell.outcomeId
            ? editor.draft
            : c.marks.filter((m): m is DeclarativeAlignmentMark => m !== 'E');
        if (declared.length > 0) alignment[c.outcomeId] = declared;
      }
      await capApi.setAlignmentMarks({
        tenantId: currentTenantId,
        courseId: editor.row.courseId,
        programId: programId!,
        alignment,
      });
      setEditor(null);
      setSavedMsg(t('matrix.saved', { course: editor.row.courseName }));
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const v = matrix?.validations;
  const outcomeLabel = (outcomeId: string): string =>
    matrix?.outcomes.find((o) => o.outcomeId === outcomeId)?.sourceOutcomeId ?? outcomeId;

  return (
    <Box>
      <CapPageHeader
        backLabel={t('matrix.back')}
        onBack={() => navigate(-1)}
        title={t('matrix.title')}
        subtitle={t('matrix.subtitle')}
      />
      <Box sx={{ px: 3, py: 3, pb: 6, maxWidth: CAP_CONTENT_MAX_WIDTH, mx: 'auto' }}>
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          <ToggleButtonGroup
            exclusive
            value={capCode}
            onChange={(_, val: CapCode | null) => val && setCapCode(val)}
            size="small"
          >
            <ToggleButton value="BSC">{t('plos.caps.BSC')}</ToggleButton>
            <ToggleButton value="DEP">{t('plos.caps.DEP')}</ToggleButton>
          </ToggleButtonGroup>
          {matrix?.rowSource === 'programCourses' && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={t('matrix.noCurriculum')}
            />
          )}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {v && (
          <Stack spacing={1} mb={2}>
            {v.outcomesWithoutEvaluation.length > 0 && (
              <Alert severity="warning">
                {t('matrix.alerts.plosWithoutE', {
                  plos: v.outcomesWithoutEvaluation.map(outcomeLabel).join(', '),
                })}
              </Alert>
            )}
            {v.coursesWithoutContribution.length > 0 && (
              <Alert severity="warning">
                {t('matrix.alerts.coursesWithoutContribution', {
                  count: v.coursesWithoutContribution.length,
                })}
              </Alert>
            )}
            {v.coursesWithoutCcd.length > 0 && (
              <Alert severity="info">
                {t('matrix.alerts.coursesWithoutCcd', {
                  courses: v.coursesWithoutCcd.map((c) => c.courseName).join(', '),
                })}
              </Alert>
            )}
            {v.orphanedSignatures.length > 0 && (
              <Alert severity="error">
                {t('matrix.alerts.orphanedSignatures', {
                  items: v.orphanedSignatures
                    .map((o) => `${o.assignmentName} → ${o.evaluatesOutcomeId}`)
                    .join(' · '),
                })}
              </Alert>
            )}
          </Stack>
        )}

        <Box sx={{ height: 4, mb: 1.5 }}>
          {loading && matrix && <LinearProgress sx={{ borderRadius: 4 }} />}
        </Box>

        {loading && !matrix ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : matrix && matrix.rows.length > 0 ? (
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, minWidth: 220 }}>
                    {t('matrix.courseHeader')}
                  </TableCell>
                  {matrix.outcomes.map((o) => (
                    <TableCell key={o.outcomeId} align="center" sx={{ fontWeight: 700 }}>
                      <Tooltip title={ploTexts[o.sourceOutcomeId] ?? ''} arrow>
                        <Box
                          component="span"
                          sx={{
                            cursor: ploTexts[o.sourceOutcomeId] ? 'help' : 'default',
                            textDecoration: ploTexts[o.sourceOutcomeId]
                              ? 'underline dotted'
                              : 'none',
                            textUnderlineOffset: 3,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {o.sourceOutcomeId}
                        </Box>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {matrix.rows.map((row) => (
                  <TableRow key={row.courseId} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {!row.hasCcd && (
                          <Tooltip title={t('matrix.rowNoCcd')}>
                            <WarningAmberRounded fontSize="small" color="warning" />
                          </Tooltip>
                        )}
                        <Typography variant="body2" fontWeight={600}>
                          {row.courseName}
                        </Typography>
                      </Stack>
                    </TableCell>
                    {row.cells.map((cell) => (
                      <TableCell
                        key={cell.outcomeId}
                        align="center"
                        onClick={(e) => openEditor(e, row, cell)}
                        sx={{
                          cursor: row.hasCcd ? 'pointer' : 'default',
                          '&:hover': row.hasCcd ? { bgcolor: 'action.hover' } : {},
                        }}
                      >
                        {cell.marks.length > 0 ? (
                          <Tooltip
                            title={
                              cell.evaluatedBy.length > 0
                                ? t('matrix.evaluatedBy', {
                                    assignments: cell.evaluatedBy
                                      .map((a) => a.assignmentName)
                                      .join(', '),
                                  })
                                : ''
                            }
                          >
                            <Typography variant="body2" fontWeight={700} letterSpacing={2}>
                              {cell.marks.join(' ')}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Typography>{t('matrix.empty')}</Typography>
          </Paper>
        )}

        {/* PLO reference — what each code means, without leaving the page. */}
        {matrix && matrix.outcomes.length > 0 && (
          <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              {t('matrix.ploReference')}
            </Typography>
            <Stack spacing={1}>
              {matrix.outcomes.map((o) => (
                <Stack key={o.outcomeId} direction="row" spacing={1.5} alignItems="flex-start">
                  <Chip
                    size="small"
                    label={o.sourceOutcomeId}
                    color="primary"
                    variant="outlined"
                    sx={{ flexShrink: 0, fontWeight: 700 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {ploTexts[o.sourceOutcomeId] ?? '—'}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        )}

        <Popover
          open={!!editor}
          anchorEl={editor?.anchor ?? null}
          onClose={() => (saving ? undefined : setEditor(null))}
        >
          {editor && (
            <Box p={2} minWidth={220}>
              <Typography variant="subtitle2" mb={1}>
                {editor.row.courseName} × {outcomeLabel(editor.cell.outcomeId)}
              </Typography>
              <Stack>
                {DECLARABLE.map((mark) => (
                  <FormControlLabel
                    key={mark}
                    control={
                      <Checkbox
                        size="small"
                        checked={editor.draft.includes(mark)}
                        onChange={(e) =>
                          setEditor({
                            ...editor,
                            draft: e.target.checked
                              ? [...editor.draft, mark]
                              : editor.draft.filter((m) => m !== mark),
                          })
                        }
                      />
                    }
                    label={t(`matrix.marks.${mark}`)}
                  />
                ))}
              </Stack>
              {editor.cell.marks.includes('E') && (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label={t('matrix.derivedE')}
                  sx={{ mt: 1 }}
                />
              )}
              <Stack direction="row" justifyContent="flex-end" spacing={1} mt={2}>
                <Button size="small" onClick={() => setEditor(null)} disabled={saving}>
                  {t('matrix.cancel')}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => void saveEditor()}
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={14} /> : t('matrix.save')}
                </Button>
              </Stack>
            </Box>
          )}
        </Popover>

        <Snackbar
          open={savedMsg !== null}
          autoHideDuration={3000}
          onClose={() => setSavedMsg(null)}
          message={savedMsg ?? ''}
        />
      </Box>
    </Box>
  );
}
